import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  emptyAddress,
  insureds,
  type Address,
  type Insured,
  type InsuredInput,
  type InsuredSector,
} from '@/shared/api/resources';
import { formatCpfCnpj } from '@/shared/lib/format';
import { AddressFields } from '@/shared/ui/AddressFields';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';

import { fieldErrorOf } from '../shared/fieldError';
import { ramos } from './rotulos';

/**
 * Cadastro do segurado: Dados Cadastrais do OnPoint (razao social, documento, ramo)
 * mais o e-mail e a marca de junta comercial que a API do OnPoint devolve.
 *
 * Na criacao, o documento e conferido assim que a pessoa sai do campo, como o
 * ValidateBeneficiaryByDocument do OnPoint: o digito verificador e o cadastro que ja
 * usa o documento. Quem decide e o servidor, que confere de novo ao gravar. Na
 * edicao o documento fica travado: ele nao muda depois do cadastro.
 */
export function InsuredForm({
  insured,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  insured?: Insured | undefined;
  submitting: boolean;
  error: Error | null;
  onSubmit: (input: InsuredInput) => void;
  onCancel: () => void;
}) {
  const editando = insured !== undefined;
  const [nome, setNome] = useState(insured?.legal_name ?? '');
  const [documento, setDocumento] = useState(
    insured ? formatCpfCnpj(insured.document) : '',
  );
  const [ramo, setRamo] = useState<InsuredSector | ''>(insured?.sector ?? '');
  const [email, setEmail] = useState(insured?.email ?? '');
  const [junta, setJunta] = useState(insured?.commercial_registry ?? false);
  const [endereco, setEndereco] = useState<Address>(emptyAddress);
  const [conferencia, setConferencia] = useState<{
    erro?: string;
    existente?: Insured | null;
  }>({});

  const apiError = error instanceof ApiError ? error : null;

  async function conferirDocumento() {
    if (editando || documento.replace(/\D/g, '') === '') return;
    try {
      const resposta = await insureds.checkRegistration(documento);
      setConferencia({ existente: resposta.existing });
    } catch (falha) {
      setConferencia({
        erro: falha instanceof Error ? falha.message : 'Documento invalido.',
      });
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const entrada: InsuredInput = {
      legal_name: nome,
      email,
      commercial_registry: junta,
      ...(ramo ? { sector: ramo } : {}),
    };
    if (!editando) {
      entrada.document = documento;
      if (endereco.street.trim() !== '') {
        entrada.address = {
          zip_code: endereco.zip_code,
          street: endereco.street,
          number: endereco.number,
          complement: endereco.complement ?? '',
          district: endereco.district,
          city: endereco.city,
          state: endereco.state,
        };
      }
    }
    onSubmit(entrada);
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      {error ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {error.message}
        </Alert>
      ) : null}

      {conferencia.existente ? (
        <Alert tone="warning" title="Este documento ja tem cadastro">
          <span>
            {conferencia.existente.legal_name} ja esta cadastrado com este documento.{' '}
            <Link
              className="font-semibold underline"
              to={`/segurados/${conferencia.existente.id}`}
            >
              Abrir o cadastro existente
            </Link>
          </span>
        </Alert>
      ) : null}

      <fieldset disabled={submitting} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Razao social ou nome"
            required
            value={nome}
            error={fieldErrorOf(apiError, 'legal_name')}
            onChange={(event) => setNome(event.target.value)}
          />
          <Field
            label="CPF ou CNPJ"
            required
            disabled={editando}
            inputMode="numeric"
            value={documento}
            hint={editando ? 'O documento nao muda depois do cadastro.' : undefined}
            error={conferencia.erro ?? fieldErrorOf(apiError, 'document')}
            onChange={(event) => {
              setDocumento(event.target.value);
              setConferencia({});
            }}
            onBlur={() => void conferirDocumento()}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Ramo"
            required
            placeholder="Publico ou privado"
            value={ramo}
            options={ramos}
            error={fieldErrorOf(apiError, 'sector')}
            onChange={(event) => setRamo(event.target.value as InsuredSector | '')}
          />
          <Field
            label="E-mail do segurado"
            type="email"
            value={email}
            error={fieldErrorOf(apiError, 'email')}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={junta}
            onChange={(event) => setJunta(event.target.checked)}
            className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
          />
          <span>Junta comercial cadastrada como segurado</span>
        </label>

        {!editando ? (
          <AddressFields
            legend="Endereco principal (opcional)"
            value={endereco}
            onChange={setEndereco}
          />
        ) : null}
      </fieldset>

      <div className="flex justify-end gap-2 border-t border-line pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" loading={submitting}>
          {editando ? 'Salvar alteracoes' : 'Cadastrar segurado'}
        </Button>
      </div>
    </form>
  );
}
